import { apiClient } from './api-client';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type RequestType = 'CONSENT_WITHDRAWAL' | 'DATA_ACCESS' | 'DATA_CORRECTION' | 'DATA_ERASURE' | 'DATA_PORTABILITY';
export type RequestStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELLED';
export type RequestPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type SortBy = 'recent' | 'oldest' | 'status' | 'type';

export interface RequestsFilters {
    page?: number;
    pageSize?: number;
    requestType?: RequestType;
    requestTypes?: RequestType[];
    requestStatus?: RequestStatus;
    requestStatuses?: RequestStatus[];
    priority?: RequestPriority;
    isUrgent?: boolean;
    search?: string;
    sortBy?: SortBy;
}

export interface RequestListItem {
    requestId: number;
    requestType: RequestType;
    requestStatus: RequestStatus;
    priority: RequestPriority;
    description: string;
    reason: string | null;
    purposeId: number | null;
    purposeName: string | null;
    purposeKey: string | null;
    estimatedCompletionDate: string | null;
    actualCompletionDate: string | null;
    createdDate: string;
    modifiedDate: string | null;
    requestedData?: any; // Parsed JSON for data element IDs
}

export interface RequestsListResponse {
    requests: RequestListItem[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
}

export interface RequestsSummary {
    total: number;
    urgent: number;
    open: number;
    closed: number;
}

export interface RequestsListApiResponse {
    success: boolean;
    data: RequestsListResponse;
    error?: string;
}

export interface RequestsSummaryApiResponse {
    success: boolean;
    data: RequestsSummary;
    error?: string;
}

export interface StatusHistoryEntry {
    statusHistoryId: number;
    oldStatus: string | null;
    newStatus: string;
    statusChangeReason: string | null;
    notes: string | null;
    changedDate: string;
    changedBy: number | null;
    changedByName: string | null;
}

export interface RequestDetails extends RequestListItem {
    statusHistory?: StatusHistoryEntry[];
}

export interface RequestDetailsApiResponse {
    success: boolean;
    data: RequestDetails;
    error?: string;
}

// ============================================================================
// REQUESTS SERVICE
// ============================================================================

class RequestsService {
    private baseUrl: string;

    constructor() {
        this.baseUrl = '/portal/rights-requests';
    }

    /**
     * Fetch summary statistics for rights requests.
     */
    async getRequestsSummary(): Promise<RequestsSummary> {
        try {
            const url = `${this.baseUrl}/summary`;
            const response = await apiClient.get<RequestsSummaryApiResponse>(url);

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch requests summary');
            }

            return response.data;
        } catch (error) {
            console.error('Error fetching requests summary:', error);
            throw error;
        }
    }

    /**
     * List rights requests with filtering and pagination.
     */
    async listRequests(filters: RequestsFilters = {}): Promise<RequestsListResponse> {
        try {
            // Build query string
            const params = new URLSearchParams();
            
            if (filters.page !== undefined) params.append('page', filters.page.toString());
            if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());
            if (filters.requestType) params.append('requestType', filters.requestType);
            if (filters.requestTypes && filters.requestTypes.length > 0) {
                filters.requestTypes.forEach(type => params.append('requestTypes', type));
            }
            if (filters.requestStatus) params.append('requestStatus', filters.requestStatus);
            if (filters.requestStatuses && filters.requestStatuses.length > 0) {
                filters.requestStatuses.forEach(status => params.append('requestStatuses', status));
            }
            if (filters.priority) params.append('priority', filters.priority);
            if (filters.isUrgent !== undefined) params.append('isUrgent', filters.isUrgent.toString());
            if (filters.search) params.append('search', filters.search);
            if (filters.sortBy) params.append('sortBy', filters.sortBy);

            const queryString = params.toString();
            const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

            const response = await apiClient.get<RequestsListApiResponse>(url);

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch requests list');
            }

            return response.data;
        } catch (error) {
            console.error('Error fetching requests list:', error);
            throw error;
        }
    }

    /**
     * Get detailed information for a specific request.
     */
    async getRequestDetails(requestId: number): Promise<RequestDetails> {
        try {
            const url = `${this.baseUrl}/${requestId}`;
            const response = await apiClient.get<RequestDetailsApiResponse>(url);

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch request details');
            }

            return response.data;
        } catch (error) {
            console.error(`Error fetching request details (${requestId}):`, error);
            throw error;
        }
    }
}

export const requestsService = new RequestsService();

export default requestsService;

